import { Injectable, OnDestroy } from "@angular/core";
import { AuthService, GenericError } from "@auth0/auth0-angular";
import {
    BehaviorSubject,
    combineLatest,
    filter,
    firstValueFrom,
    from,
    map,
    mergeMap,
    Observable,
    Subscription,
    tap,
} from "rxjs";
import UserService from "../Common/services/user.service";
import { authCodeFlowConfig } from "./auth-config";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { SessionExpiredDialogComponent } from "../Common/session-expired-dialog/session-expired-dialog.component";
import * as Sentry from "@sentry/angular";

@Injectable({
    providedIn: "root",
})
export class ArcusAuthService implements OnDestroy {
    _isAuthenticated: boolean = false;
    _isAuthenticatedWithAccount$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    _isAnonymous$: BehaviorSubject<boolean> = new BehaviorSubject(true);

    private subs: Subscription[] = [];
    private _sessionExpiredDialogOpen = false;

    constructor(
        private http: HttpClient,
        private auth: AuthService,
        private userService: UserService,
        private router: Router,
        private dialog: MatDialog
    ) {
        this.subs.push(
            this.auth.error$
                .pipe(
                    filter(
                        (e) =>
                            e instanceof GenericError &&
                            (e.error === "login_required" ||
                                e.error === "mfa_required" ||
                                e.error === "access_denied" ||
                                e.error === "timeout" ||
                                e.error === "consent_required")
                    ),
                    mergeMap((e: GenericError) => {
                        if (e.error === "login_required") {
                            this.handleSessionExpired();
                            return [];
                        }

                        if (e.error === "mfa_required") {
                            this.handleMfaRequired();
                            return [];
                        }

                        // Auth0's hidden-iframe background token refresh timed out — same treatment as
                        // the HTTP-triggered "Timeout" case in AuthInterceptorService.
                        if (e.error === "timeout") {
                            this.handleSessionExpired();
                            return [];
                        }

                        // User needs to (re-)grant API consent — prompt them to re-authenticate rather
                        // than letting this surface as an uncaught error.
                        if (e.error === "consent_required") {
                            return this.auth.loginWithRedirect();
                        }

                        if (
                            e.error === "access_denied" &&
                            e.error_description?.includes("Your password has expired.  Please reset it.")
                        ) {
                            return from(this.auth.logout()).pipe(
                                mergeMap(() =>
                                    this.auth.loginWithRedirect({
                                        authorizationParams: {
                                            screen_hint: "reset password",
                                            prompt: "login",
                                        },
                                    })
                                )
                            );
                        }

                        // Fallback for any other access_denied error
                        return this.auth.loginWithRedirect();
                    })
                )
                .subscribe()
        );

        this.auth.isAuthenticated$.subscribe((isLoggedIn) => {
            if (isLoggedIn) {
                this._isAnonymous$.next(false);
                this._sessionExpiredDialogOpen = false;
                this.userService.currentUser().then(() => {
                    this._isAuthenticatedWithAccount$.next(isLoggedIn);
                });
            } else {
                this._isAnonymous$.next(true);
                this._isAuthenticatedWithAccount$.next(isLoggedIn);
            }
        });
    }

    ngOnDestroy(): void {
        this.subs.forEach((sub) => sub.unsubscribe());
    }

    get isAuthenticated(): Boolean {
        return this._isAuthenticated;
    }

    get isCoreAuthenticated$(): Observable<boolean> {
        return this.auth.isAuthenticated$;
    }

    get isAnonymous$(): Observable<boolean> {
        return this._isAnonymous$;
    }

    get isAuthenticated$(): Observable<boolean> {
        return combineLatest([this.auth.isAuthenticated$, this._isAuthenticatedWithAccount$]).pipe(
            map(([isLoggedIn, isAccountLoaded]) => isLoggedIn && isAccountLoaded),
            tap((isAuthenticated) => (this._isAuthenticated = isAuthenticated))
        );
    }

    get isPasswordExpired$(): Observable<boolean> {
        return this.auth.user$.pipe(map((user) => !!user && user["Password-Expired"] === true));
    }

    public getAccessToken(): Observable<string> {
        return this.auth.getAccessTokenSilently();
    }

    public login(targetUrl?: string, userType: string = "broker") {
        let authParams = {};
        switch (userType.toLowerCase()) {
            case "customer":
                authParams = { connection: "email" };
                break;
            case "carrier":
            case "factor":
            case "broker":
            default:
                authParams = { connection: "Username-Password-Authentication" };
        }
        //, authorizationParams: { connection: "email" }
        this.auth.loginWithRedirect({ appState: { target: targetUrl }, authorizationParams: authParams });
    }

    public logout() {
        this.auth.logout({ logoutParams: { returnTo: document.location.origin } });
    }

    /** Called when Auth0 signals login_required. Shows a non-disruptive dialog so the user can
     *  re-authenticate via popup without losing their place. Only one dialog is shown at a time
     *  even if multiple in-flight requests fail simultaneously after token expiry. */
    public handleSessionExpired(): void {
        if (this._sessionExpiredDialogOpen) return;
        this._sessionExpiredDialogOpen = true;

        Sentry.captureMessage("Session expired — user prompted to re-authenticate", {
            level: "info",
            fingerprint: ["session-expired"],
            tags: { type: "session-expired", url: window.location.pathname },
        });

        const ref = this.dialog.open(SessionExpiredDialogComponent, {
            maxWidth: "420px",
            disableClose: true,
        });

        ref.afterClosed().subscribe(() => {
            this._sessionExpiredDialogOpen = false;
        });
    }

    public handleMfaRequired(): void {
        Sentry.captureMessage("MFA required — redirecting user to complete challenge", {
            level: "info",
            fingerprint: ["mfa-required"],
            tags: { type: "mfa-required", url: window.location.pathname },
        });
        this.auth.loginWithRedirect({
            appState: { target: this.router.url },
            authorizationParams: { prompt: "login" },
        });
    }

    async changeRequest(): Promise<void> {
        const user = await firstValueFrom(this.auth.user$);
        if (!user?.email) throw new Error("User email not available");

        const hasProfile = await firstValueFrom(this.isAuthenticated$);
        if (!hasProfile) throw new Error("Arcus profile not available");

        const url = `https://${authCodeFlowConfig.domain}/dbconnections/change_password`;

        const body = {
            client_id: authCodeFlowConfig.clientId,
            email: user?.email,
            connection: "Username-Password-Authentication",
        };

        await firstValueFrom(this.http.post(url, body, { responseType: "text" }));
    }
}
